import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { useAuth } from '../../../contexts/AuthContext';

import { DEFAULT_CENTER_COORDS, getPlaceLatLng, INITIAL_ZOOM_LEVEL } from './RouteMap.helpers';

interface IUseRouteMapDetails {
  isDesktop?: boolean;
  mapInstance?: google.maps.Map;
  useRideEditor: () => { waypointsOnMap: WaypointMarker[]; completeAnchorTime?: Date };
}

export const useMapCenter = ({ useRideEditor, mapInstance, isDesktop = true }: IUseRouteMapDetails) => {
  const [center, setCenter] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER_COORDS);
  const { waypointsOnMap } = useRideEditor();
  const { userSettings } = useAuth();

  const getAccountSettingsLocation = useCallback(async () => {
    if (userSettings && mapInstance) {
      const response = await getPlaceLatLng(userSettings.billingAddress?.replace(', USA', ''), mapInstance);

      response && setCenter(response);
    }
  }, [getPlaceLatLng, mapInstance]);

  const fitMapBounds = useCallback(() => {
    if (mapInstance && waypointsOnMap.length) {
      const waypointsCoords = waypointsOnMap
        .filter((waypoint) => waypoint.location)
        .map((waypoint) => waypoint.latLng?.split(',') || '');

      if (waypointsCoords.length) {
        const bounds = new window.google.maps.LatLngBounds();

        waypointsCoords.forEach((latLng) => {
          bounds.extend({
            lat: parseFloat(latLng[0]),
            lng: parseFloat(latLng[1]),
          });
        });
        // paddings were added in order to fit any kind of route length and orientation
        mapInstance.fitBounds(bounds, {
          top: 100,
          right: 100,
          bottom: isDesktop ? 300 : 100,
          left: 100,
        });
        mapInstance.panBy(0, 50);
        waypointsCoords.length <= 1 && mapInstance.setZoom(17);
      } else {
        mapInstance.setCenter(center);
        mapInstance.setZoom(INITIAL_ZOOM_LEVEL);
      }
    }
  }, [center, mapInstance, waypointsOnMap]);

  const noWaypointsWithLocation = !waypointsOnMap.find((waypoint) => waypoint.location);

  useEffect(() => {
    center.lat === DEFAULT_CENTER_COORDS.lat &&
      center.lng === DEFAULT_CENTER_COORDS.lng &&
      noWaypointsWithLocation &&
      getAccountSettingsLocation();
  }, [getAccountSettingsLocation, noWaypointsWithLocation]);

  useEffect(() => {
    debounce(fitMapBounds, 800)();
  }, [fitMapBounds]);

  return { center };
};
